/**
 * MY WORKPLACES
 * 
 * Shows all businesses where the user has staff access.
 * - Linked through their Mango Personal ID
 * - One account, multiple workplaces
 * - Role-based access to each business's staff dashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workplacesApi } from '../services/api';

interface Workplace {
  id: string;
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  businessMangoId: string;
  role: string;
  permissions: string[];
  startDate: string;
  status: 'active' | 'pending' | 'suspended';
  lastClockIn?: string;
  todayHours?: number;
  weekHours?: number;
  features: string[]; // Which features they can access
}

interface WorkplaceInvitation {
  id: string;
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  role: string;
  message?: string;
  invitedAt: string;
  expiresAt: string;
}

const Workplaces: React.FC = () => {
  const navigate = useNavigate();
  const { refreshStaffAccounts } = useAuth();
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [invitations, setInvitations] = useState<WorkplaceInvitation[]>([]);
  const [pastWorkplaces, setPastWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'invitations' | 'history'>('current');
  const [acceptConfirm, setAcceptConfirm] = useState<WorkplaceInvitation | null>(null);

  useEffect(() => {
    loadWorkplaces();
    refreshStaffAccounts?.();
  }, [refreshStaffAccounts]);

  const loadWorkplaces = async () => {
    try {
      setLoading(true);
      setError(null);

      const [workplacesRes, invitationsRes, historyRes] = await Promise.all([
        workplacesApi.getMyWorkplaces(),
        workplacesApi.getPendingInvitations(),
        workplacesApi.getWorkHistory(),
      ]);

      if (workplacesRes.success) {
        setWorkplaces(workplacesRes.data || []);
      }
      if (invitationsRes.success) {
        setInvitations(invitationsRes.data || []);
      }
      if (historyRes.success) {
        setPastWorkplaces(historyRes.data || []);
      }
    } catch (err) {
      console.error('Failed to load workplaces:', err);
      setError('Failed to load workplaces');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = (invitation: WorkplaceInvitation) => {
    setAcceptConfirm(invitation);
  };

  const confirmAcceptInvitation = async () => {
    if (!acceptConfirm) return;
    try {
      const response = await workplacesApi.acceptInvitation(acceptConfirm.id);
      if (response.success) {
        setAcceptConfirm(null);
        loadWorkplaces(); // Refresh
      }
    } catch (err) {
      console.error('Failed to accept invitation:', err);
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      const response = await workplacesApi.declineInvitation(invitationId);
      if (response.success) {
        loadWorkplaces(); // Refresh
      }
    } catch (err) {
      console.error('Failed to decline invitation:', err);
    }
  };

  const handleOpenWorkplace = (workplace: Workplace) => {
    // Store the active workplace context
    localStorage.setItem('activeWorkplace', JSON.stringify({
      businessId: workplace.businessId,
      businessName: workplace.businessName,
      role: workplace.role,
      permissions: workplace.permissions,
      features: workplace.features,
    }));

    // Navigate to staff dashboard
    navigate(`/workplace/${workplace.businessId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatHours = (hours?: number) => {
    if (!hours) return '0h';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const getRoleColor = (role: string) => {
    const colors: Record<string, string> = {
      owner: '#FFD700',
      manager: '#4169E1',
      staff: '#32CD32',
      contractor: '#FF6347',
    };
    return colors[role.toLowerCase()] || '#888';
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FFF8E7' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>💼</div>
          <p>Loading workplaces...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#FFF8E7', paddingBottom: '80px' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
        padding: '2rem 1.5rem',
        borderBottomLeftRadius: '2rem',
        borderBottomRightRadius: '2rem',
      }}>
        <h1 style={{ fontSize: '1.75rem', fontWeight: 'bold', margin: 0 }}>💼 My Workplaces</h1>
        <p style={{ margin: '0.5rem 0 0', opacity: 0.8 }}>
          {workplaces.length} active {workplaces.length === 1 ? 'workplace' : 'workplaces'}
          {invitations.length > 0 && ` • ${invitations.length} pending`}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', padding: '1rem', gap: '0.5rem' }}>
        {(['current', 'invitations', 'history'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.5rem',
              background: activeTab === tab ? '#FFD700' : 'white',
              fontWeight: activeTab === tab ? 'bold' : 'normal',
              cursor: 'pointer',
              position: 'relative',
            }}
          >
            {tab === 'current' && '🏢 Current'}
            {tab === 'invitations' && (
              <>
                ✉️ Invitations
                {invitations.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-5px',
                    right: '-5px',
                    background: '#FF6347',
                    color: 'white',
                    borderRadius: '50%',
                    width: '20px',
                    height: '20px',
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {invitations.length}
                  </span>
                )}
              </>
            )}
            {tab === 'history' && '📜 History'}
          </button>
        ))}
      </div>

      <div style={{ padding: '0 1rem' }}>
        {error && (
          <div style={{
            background: '#FFE4E4',
            color: '#D00',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        {/* Current Workplaces */}
        {activeTab === 'current' && (
          <div>
            {workplaces.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                background: 'white',
                borderRadius: '1rem',
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏢</div>
                <h3 style={{ margin: '0 0 0.5rem' }}>No Active Workplaces</h3>
                <p style={{ color: '#666', margin: 0 }}>
                  When a business adds you as staff, it will appear here.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {workplaces.map(workplace => (
                  <div
                    key={workplace.id}
                    onClick={() => handleOpenWorkplace(workplace)}
                    style={{
                      background: 'white',
                      borderRadius: '1rem',
                      padding: '1rem',
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#FFD700')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = 'transparent')}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      {/* Logo */}
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '0.75rem',
                        background: workplace.businessLogoUrl 
                          ? `url(${workplace.businessLogoUrl}) center/cover`
                          : 'linear-gradient(135deg, #FFD700, #FFA500)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                      }}>
                        {!workplace.businessLogoUrl && '🥭'}
                      </div>

                      {/* Info */}
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>
                          {workplace.businessName}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{
                            background: getRoleColor(workplace.role),
                            color: workplace.role.toLowerCase() === 'owner' ? '#000' : '#fff',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                          }}>
                            {workplace.role}
                          </span>
                          <span style={{ fontSize: '0.8rem', color: '#666' }}>
                            Since {formatDate(workplace.startDate)}
                          </span>
                        </div>
                      </div>

                      {/* Arrow */}
                      <div style={{ fontSize: '1.5rem', color: '#FFD700' }}>→</div>
                    </div>

                    {/* Stats */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '0.5rem',
                      marginTop: '1rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid #eee',
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>Today</div>
                        <div style={{ fontWeight: 'bold' }}>{formatHours(workplace.todayHours)}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>This Week</div>
                        <div style={{ fontWeight: 'bold' }}>{formatHours(workplace.weekHours)}</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>Features</div>
                        <div style={{ fontWeight: 'bold' }}>{workplace.features.length}</div>
                      </div>
                    </div>

                    {/* Quick Access */}
                    <div style={{
                      display: 'flex',
                      gap: '0.5rem',
                      marginTop: '0.75rem',
                      flexWrap: 'wrap',
                    }}>
                      {workplace.features.slice(0, 4).map(feature => (
                        <span key={feature} style={{
                          background: '#FFF8E7',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.7rem',
                        }}>
                          {feature === 'pos' && '💳 POS'}
                          {feature === 'schedule' && '📅 Schedule'}
                          {feature === 'spending' && '💰 Spending'}
                          {feature === 'customers' && '👥 Customers'}
                          {feature === 'orders' && '📦 Orders'}
                          {feature === 'content' && '📱 Content'}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Invitations */}
        {activeTab === 'invitations' && (
          <div>
            {invitations.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                background: 'white',
                borderRadius: '1rem',
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✉️</div>
                <h3 style={{ margin: '0 0 0.5rem' }}>No Pending Invitations</h3>
                <p style={{ color: '#666', margin: 0 }}>
                  Job invitations from businesses will appear here.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {invitations.map(invitation => (
                  <div
                    key={invitation.id}
                    style={{
                      background: 'white',
                      borderRadius: '1rem',
                      padding: '1rem',
                      border: '2px solid #FFD700',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '0.75rem',
                        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                      }}>
                        🥭
                      </div>
                      <div style={{ flex: 1 }}>
                        <h3 style={{ margin: '0 0 0.25rem' }}>{invitation.businessName}</h3>
                        <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                          Invited as <strong>{invitation.role}</strong>
                        </p>
                      </div>
                    </div>

                    {invitation.message && (
                      <p style={{
                        background: '#FFF8E7',
                        padding: '0.75rem',
                        borderRadius: '0.5rem',
                        margin: '0 0 1rem',
                        fontStyle: 'italic',
                      }}>
                        "{invitation.message}"
                      </p>
                    )}

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleAcceptInvitation(invitation)}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: '#FFD700',
                          border: 'none',
                          borderRadius: '0.5rem',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                        }}
                      >
                        ✓ Accept
                      </button>
                      <button
                        onClick={() => handleDeclineInvitation(invitation.id)}
                        style={{
                          flex: 1,
                          padding: '0.75rem',
                          background: '#f0f0f0',
                          border: 'none',
                          borderRadius: '0.5rem',
                          cursor: 'pointer',
                        }}
                      >
                        ✕ Decline
                      </button>
                    </div>

                    <p style={{ margin: '0.75rem 0 0', fontSize: '0.75rem', color: '#999', textAlign: 'center' }}>
                      Expires {formatDate(invitation.expiresAt)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Work History */}
        {activeTab === 'history' && (
          <div>
            {pastWorkplaces.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                background: 'white',
                borderRadius: '1rem',
              }}>
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📜</div>
                <h3 style={{ margin: '0 0 0.5rem' }}>No Work History</h3>
                <p style={{ color: '#666', margin: 0 }}>
                  Your past employment at Mango businesses will be shown here.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {pastWorkplaces.map(workplace => (
                  <div
                    key={workplace.id}
                    style={{
                      background: 'white',
                      borderRadius: '0.75rem',
                      padding: '1rem',
                      opacity: 0.8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '0.5rem',
                        background: '#e0e0e0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        🏢
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 0.25rem' }}>{workplace.businessName}</h4>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#666' }}>
                          {workplace.role} • {formatDate(workplace.startDate)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Accept confirmation modal - double handshake */}
        {acceptConfirm && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}>
            <div style={{
              background: 'white',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '400px',
              textAlign: 'center',
            }}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '18px' }}>Confirm staff invitation</h3>
              <p style={{ margin: '0 0 16px 0', color: '#666', fontSize: '14px' }}>
                By accepting, you&apos;re giving <strong>{acceptConfirm.businessName}</strong> access to your shareable profile (bio, contacts, bank details, etc.) from My Profile.
              </p>
              <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#888' }}>
                Double handshake: they invited you → you confirm → they get the info.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button
                  onClick={() => setAcceptConfirm(null)}
                  style={{ padding: '10px 20px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAcceptInvitation}
                  style={{ padding: '10px 20px', background: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Yes, Accept
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Workplaces;
